import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setBearer, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = loading, false = anonymous, object = user/tenant
  const [state, setState] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setState({ user: data.user, tenant: data.tenant });
    } catch (e) {
      setState(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password, extras = {}) => {
    try {
      const body = { email, password };
      if (extras.totp_code) body.totp_code = extras.totp_code;
      if (extras.backup_code) body.backup_code = extras.backup_code;
      const { data } = await api.post("/auth/login", body);
      // Server signalled we need a 2FA code — no tokens issued yet.
      if (data.twofa_required) {
        return { ok: false, twofa_required: true };
      }
      if (data.access_token) setBearer(data.access_token);
      await refresh();
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, [refresh]);

  const registerTenant = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/auth/register-tenant", payload);
      if (data.access_token) setBearer(data.access_token);
      await refresh();
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, [refresh]);

  const logout = useCallback(async () => {
    // Best-effort — if the network call fails we still clear the local
    // session below so the user isn't left in a half-authenticated state.
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.debug("[auth] logout request failed (clearing local state anyway)", err);
    }
    setBearer(null);
    setState(false);
  }, []);

  return (
    <AuthContext.Provider value={{ state, login, logout, registerTenant, refresh }}>
      {children}
      <Enrol2FAModalMount />
    </AuthContext.Provider>
  );
}

// Lazy-mounted so unauthenticated pages (login, public signup) don't
// pull the modal component into their bundle. Also keeps the modal out
// of the AuthContext file's dependency graph.
function Enrol2FAModalMount() {
  const [Cmp, setCmp] = useState(null);
  useEffect(() => {
    import("@/components/Enrol2FAModal").then((m) => setCmp(() => m.default));
  }, []);
  if (!Cmp) return null;
  return <Cmp />;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
